import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { cacheService } from '../services/cache/index';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';

import '../contracts/FormasPagamento_AtualizarResponse';
import '../contracts/FormasPagamento_BuscaFormasPagtoResponse';
import '../contracts/FormasPagamento_BuscarResponse';
import '../contracts/FormasPagamento_CriarResponse';
import '../contracts/FormasPagamento_DeletarResponse';
import '../contracts/FormasPagamento_ListaResponse';
import '../contracts/FormasPagamento_AtualizarInput';
import '../contracts/FormasPagamento_BuscaFormasPagtoInput';
import '../contracts/FormasPagamento_CriarInput';
import '../contracts/FormasPagamento_DeletarInput';
import '../contracts/FormasPagamento_ListaInput';
import '../contracts/FormasPagamentoFranquia_ListaResponse';
import '../contracts/FormasPagamentoFranquia_SalvarResponse';
import '../contracts/FormasPagamentoFranquia_RemoverResponse';
import '../contracts/FormasPagamentoFranquia_ListaInput';
import '../contracts/FormasPagamentoFranquia_SalvarInput';
import '../contracts/FormasPagamentoFranquia_RemoverInput';
import type { FormasPagamento_AtualizarInput } from '../contracts/FormasPagamento_AtualizarInput';
import type { FormasPagamento_CriarInput } from '../contracts/FormasPagamento_CriarInput';
import type { FormasPagamento_ListaInput } from '../contracts/FormasPagamento_ListaInput';
import type { FormasPagamentoFranquia_SalvarInput } from '../contracts/FormasPagamentoFranquia_SalvarInput';
import type { FormasPagamentoFranquia_RemoverInput } from '../contracts/FormasPagamentoFranquia_RemoverInput';
/**
 * Composable for Formas de Pagamento (Payment Methods) operations
 * Provides methods to interact with the Payment Methods API
 *
 * Caching: Payment methods are cached for 60 minutes (configured in cacheConfig.js)
 * Cache is invalidated on create/update/delete via 'formasPagamento.updated' event
 */
export function useFormasPagamento() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all payment methods (with caching)
   * @param {boolean} forceRefresh - Force refresh from API, bypassing cache
   * @param {number} franquiaId - Devolve cada forma como vale nesta franquia (chamado 2026-0023).
   *   Essa lista não passa pelo cache: a tela de venda não pode oferecer o que o backend recusa.
   */
  const listarFormasPagamento = async (forceRefresh: boolean = false, franquiaId?: number | null) => {
    log('useFormasPagamento', 'listarFormasPagamento', 'Fetching payment methods', { forceRefresh, franquiaId });
    isLoading.value = true;
    error.value = null;

    const buscar = async (body: FormasPagamento_ListaInput) => {
      const response = await httpService.post('/index.php/config/formas_pagamento/lista', body, { responseContract: 'FormasPagamento_ListaResponse', inputContract: 'FormasPagamento_ListaInput' });

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao listar formas de pagamento');
      }
    };

    try {
      if (franquiaId) {
        return await buscar({ franquia_id: franquiaId });
      }

      // If force refresh, invalidate cache first
      if (forceRefresh) {
        await cacheService.invalidate('formasPagamento');
      }

      // Use cache service - will fetch from API if not cached or expired
      const data = await cacheService.get('formasPagamento', () => {
        log('useFormasPagamento', 'listarFormasPagamento', 'Cache miss, fetching from API');
        return buscar({} as FormasPagamento_ListaInput);
      });

      log('useFormasPagamento', 'listarFormasPagamento', 'Data retrieved', { count: data.length });
      return data;
    } catch (err) {
      log('useFormasPagamento', 'listarFormasPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single payment method by ID (not cached - individual lookups)
   */
  const buscarFormaPagamento = async (formaPagamentoId: number) => {
    log('useFormasPagamento', 'buscarFormaPagamento', 'Fetching payment method', formaPagamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/formas_pagamento/buscar/${formaPagamentoId}`, { responseContract: 'FormasPagamento_BuscarResponse' });
      log('useFormasPagamento', 'buscarFormaPagamento', 'Response', response);

      if (response.success) {
        return response.dados || null;
      } else {
        throw new Error(response.error || 'Erro ao buscar forma de pagamento');
      }
    } catch (err) {
      log('useFormasPagamento', 'buscarFormaPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new payment method
   * Emits 'formasPagamento.updated' to invalidate cache
   */
  const criarFormaPagamento = async (data: FormasPagamento_CriarInput) => {
    log('useFormasPagamento', 'criarFormaPagamento', 'Creating payment method', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/formas_pagamento/criar', data, {
        responseContract: 'FormasPagamento_CriarResponse',
        inputContract: 'FormasPagamento_CriarInput'
      });
      log('useFormasPagamento', 'criarFormaPagamento', 'Response', response);

      if (response.success) {
        // Invalidate cache after successful creation
        eventBus.emit('formasPagamento.updated');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar forma de pagamento');
      }
    } catch (err) {
      log('useFormasPagamento', 'criarFormaPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update existing payment method
   * Emits 'formasPagamento.updated' to invalidate cache
   */
  const atualizarFormaPagamento = async (formaPagamentoId: number | string, data: Omit<FormasPagamento_AtualizarInput, 'forma_pagamento_id'>) => {
    log('useFormasPagamento', 'atualizarFormaPagamento', 'Updating payment method', formaPagamentoId, data);
    isLoading.value = true;
    error.value = null;

    try {
      const payload = {
        ...data,
        forma_pagamento_id: formaPagamentoId
      };

      const response = await httpService.post('/index.php/config/formas_pagamento/atualizar', payload, {
        responseContract: 'FormasPagamento_AtualizarResponse',
        inputContract: 'FormasPagamento_AtualizarInput'
      });
      log('useFormasPagamento', 'atualizarFormaPagamento', 'Response', response);

      if (response.success) {
        // Invalidate cache after successful update
        eventBus.emit('formasPagamento.updated');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar forma de pagamento');
      }
    } catch (err) {
      log('useFormasPagamento', 'atualizarFormaPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete payment method
   * Emits 'formasPagamento.updated' to invalidate cache
   */
  const deletarFormaPagamento = async (formaPagamentoId: number) => {
    log('useFormasPagamento', 'deletarFormaPagamento', 'Deleting payment method', formaPagamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/formas_pagamento/deletar', {
        forma_pagamento_id: formaPagamentoId
      }, {
        responseContract: 'FormasPagamento_DeletarResponse',
        inputContract: 'FormasPagamento_DeletarInput'
      });
      log('useFormasPagamento', 'deletarFormaPagamento', 'Response', response);

      if (response.success) {
        // Invalidate cache after successful deletion
        eventBus.emit('formasPagamento.updated');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao deletar forma de pagamento');
      }
    } catch (err) {
      log('useFormasPagamento', 'deletarFormaPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get payment methods as key-value pairs for dropdowns (with caching)
   * Uses a separate cache key 'formasPagamentoKV' with same TTL
   * @param {boolean} forceRefresh - Force refresh from API, bypassing cache
   */
  const kvFormasPagamento = async (forceRefresh: boolean = false) => {
    log('useFormasPagamento', 'kvFormasPagamento', 'Fetching key-value payment methods', { forceRefresh });
    isLoading.value = true;
    error.value = null;

    try {
      // If force refresh, invalidate cache first
      if (forceRefresh) {
        await cacheService.invalidate('formasPagamentoKV');
      }

      // Use cache service with same config as formasPagamento
      const data = await cacheService.get('formasPagamentoKV', async () => {
        log('useFormasPagamento', 'kvFormasPagamento', 'Cache miss, fetching from API');
        const response = await httpService.post('/index.php/config/formas_pagamento/buscaFormasPagto', {}, { responseContract: 'FormasPagamento_BuscaFormasPagtoResponse', inputContract: 'FormasPagamento_BuscaFormasPagtoInput' });

        if (response.success) {
          return response.dados || {};
        } else {
          throw new Error(response.error || 'Erro ao buscar formas de pagamento');
        }
      }, {
        // Use same config as formasPagamento
        ttl: 60 * 60 * 1000,
        events: ['formasPagamento.updated']
      });

      log('useFormasPagamento', 'kvFormasPagamento', 'Data retrieved', { keys: Object.keys(data).length });
      return data;
    } catch (err) {
      log('useFormasPagamento', 'kvFormasPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** As formas com o padrão global e o que esta franquia sobrescreveu (chamado 2026-0023). */
  const listarAplicacoes = async (franquiaId: number) => {
    log('useFormasPagamento', 'listarAplicacoes', 'Fetching franchise overrides', franquiaId);
    const response = await httpService.post('/index.php/config/formas_pagamento_franquias/lista', { franquia_id: franquiaId }, {
      responseContract: 'FormasPagamentoFranquia_ListaResponse',
      inputContract: 'FormasPagamentoFranquia_ListaInput'
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao listar as formas de pagamento da franquia');
    }
    return response.dados || [];
  };

  /** Grava a aplicação inteira: campo null herda o padrão global. */
  const salvarAplicacao = async (data: FormasPagamentoFranquia_SalvarInput) => {
    log('useFormasPagamento', 'salvarAplicacao', 'Saving franchise override', data);
    const response = await httpService.post('/index.php/config/formas_pagamento_franquias/salvar', data, {
      responseContract: 'FormasPagamentoFranquia_SalvarResponse',
      inputContract: 'FormasPagamentoFranquia_SalvarInput'
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao salvar a forma de pagamento da franquia');
    }
    eventBus.emit('formasPagamentoFranquia.updated');
    return response.dados;
  };

  /** Apaga a aplicação: a forma volta a herdar o padrão global. */
  const removerAplicacao = async (data: FormasPagamentoFranquia_RemoverInput) => {
    log('useFormasPagamento', 'removerAplicacao', 'Removing franchise override', data);
    const response = await httpService.post('/index.php/config/formas_pagamento_franquias/remover', data, {
      responseContract: 'FormasPagamentoFranquia_RemoverResponse',
      inputContract: 'FormasPagamentoFranquia_RemoverInput'
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao restaurar o padrão da forma de pagamento');
    }
    eventBus.emit('formasPagamentoFranquia.updated');
    return response.dados;
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    listarFormasPagamento,
    buscarFormaPagamento,
    criarFormaPagamento,
    atualizarFormaPagamento,
    deletarFormaPagamento,

    // Aplicação por franquia
    listarAplicacoes,
    salvarAplicacao,
    removerAplicacao,

    // Helper methods
    kvFormasPagamento
  };
}
